// src/services/api.ts
import axios from 'axios';
import { Result, GenerateUser, UniqueUser, LicenseTransactionEntity, LicServer ,ToolEntity} from '../types';
import { store } from '../store';
import { addError } from '../store/errorSlice';
import { setUser, logoutUser } from '../store/authSlice';

// In development, don't set baseURL so requests go through webpack proxy
// In production, use the actual backend URL
const isProduction = process.env.NODE_ENV === 'production';

const isTestMode = process.env.PORT === '3001' ||
                     process.env.NODE_ENV === 'test' ||
                     process.env.REACT_APP_TEST_MODE === 'true';

// Production backend URL:
// - unset / missing → legacy default localhost:3400
// - "" or "same-origin" → empty axios baseURL (relative URLs; trg1 nginx
//   routes API paths to pma-backend — pmav2 single-gateway mode)
// - any other string → absolute API origin (legacy pmaapi.* hostname)
const resolveBackendUrl = (): string => {
  if (!isProduction) {
    if (isTestMode) return 'http://localhost:3401';
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:3400';
  }
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw === '' || raw === 'same-origin') return '';
  return raw || 'http://localhost:3400';
};

const backendUrl = resolveBackendUrl();

/** Origin for window.location / raw axios outside the api client. Empty = same origin. */
export function backendPublicOrigin(): string {
  return resolveBackendUrl();
}

const api = axios.create({
  baseURL: backendUrl,
  withCredentials: true,
});

// In development, baseURL is empty so requests use relative URLs and go through webpack proxy
// In production, baseURL uses REACT_APP_BACKEND_URL for direct API calls

// Request interceptor to add JWT token to headers
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global error interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Extract error details
    const errorDetails = {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      url: error.config?.url,
      method: error.config?.method?.toUpperCase(),
    };

    // Determine error type and message
    let errorType: 'api' | 'validation' | 'network' | 'operation' = 'api';
    let title = 'API Error';
    let message = 'An error occurred while communicating with the server';

    if (error.code === 'NETWORK_ERROR' || !error.response) {
      errorType = 'network';
      title = 'Network Error';
      message = 'Unable to connect to the server. Please check your internet connection.';
    } else if (error.response?.status === 400) {
      errorType = 'validation';
      title = 'Validation Error';
      message = error.response.data?.message || 'Invalid request data';
    } else if (error.response?.status >= 500) {
      title = 'Server Error';
      message = 'The server encountered an error. Please try again later.';
    } else if (error.response?.status === 401) {
      title = 'Authentication Error';
      message = 'Your session has expired. Please log in again.';
      // Clear auth state so ProtectedRoute redirects to login and other pages don't render with stale user
      store.dispatch(logoutUser());
    } else if (error.response?.status === 403) {
      title = 'Permission Error';
      message = 'You do not have permission to perform this action.';
    }

    // Add error to store
    store.dispatch(addError({
      type: errorType,
      severity: error.response?.status >= 500 ? 'error' : 'warning',
      title,
      message,
      details: errorDetails,
    }));

    return Promise.reject(error);
  }
);

export const register = (data: any) => api.post('/auth/register', data);
// Store the token internally in localStorage after successful login
export const login = async (data: any) => {
  try {
    const response = await api.post('/auth/login',
            data, {
                withCredentials: true,
            }
    );
    console.log('Date retrieved back on login:', data)
    console.log("my response is ---------------------------------------->" , response);

    // Store JWT token if provided in response
    console.log('🔍 Login response data:', response.data);
    const token = response.data?.token || response.data?.accessToken;
    console.log('🔑 Extracted token:', token ? token.substring(0, 20) + '...' : 'null');

    if (token) {
      localStorage.setItem('authToken', token);
      console.log('✅ JWT token stored in localStorage');

      // Store user data in Redux and localStorage
      if (response.data?.user) {
        console.log('👤 User data to store:', response.data.user);
        // Dispatch to Redux store
        store.dispatch(setUser(response.data.user));
        console.log('✅ User data stored in Redux and localStorage');
      } else {
        console.log('⚠️ No user data in login response');
      }

      // Verify storage
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      console.log('🔍 Verification - token stored:', !!storedToken, 'user stored:', !!storedUser);
    } else {
      console.log('❌ No token received in login response - cannot store authentication');
    }

    console.log('Response for login: ', response?.data);
    return response?.data;
  } catch (error) {
    console.error('Login failed:', error);
    throw error;
  }
};


// Logout function that removes the token from localStorage
export const logout = async () => {
  try {
    await api.get('/auth/logout');
    localStorage.removeItem('authToken'); // ✅ CRITICAL: Remove JWT token to prevent reuse
    console.log('JWT token cleared from localStorage during logout');
  } catch (error) {
    console.error('Logout failed:', error);
    throw error;
  }
};

// TODO: LATER ON Fetch the current user based on the stored token (JWT etc)
export const getCurrentUser = async () => {
  try {
    console.log('API for getCurrentUser being executed');
    const response = await api.get('/auth/me', { withCredentials: true });
    console.log('getCurrentUser Response:', response.data);
    return response.data; // Return the data directly
  } catch (error) {
    console.error('Fetching current user failed:', error);
    throw error;
  }
};

export interface RefreshInfraUsersError extends Error {
  failureLog?: string;
  responseMessage?: string;
  code?: string; // e.g. 'ECONNABORTED' from axios on timeout
}

// Refresh starts async (202 + operationId); poll status until terminal.
// Client axios timeout only needs to cover the short start request.
const REFRESH_INFRA_USERS_START_TIMEOUT_MS = Number(
  process.env.REACT_APP_REFRESH_INFRA_USERS_START_TIMEOUT_MS || 60000
);

export interface RefreshInfraUsersStartResponse {
  message: string;
  operationId: string;
  fetchId: string;
  status: string;
  statusUrl?: string;
}

export const refreshInfraUsers = async (
  remarks: string,
  source: string
): Promise<RefreshInfraUsersStartResponse> => {
  try {
    console.log('Executing /infrauser/prefresh API call for remarks:', remarks, 'and source:', source);
    const data = { remarks: remarks, source: source };
    const response = await api.post<RefreshInfraUsersStartResponse>(
      '/infrauser/prefresh',
      data,
      { withCredentials: true, timeout: REFRESH_INFRA_USERS_START_TIMEOUT_MS }
    );
    console.log('Refresh InfraUser started:', response.data);
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: unknown; status?: number }; message?: string };
    const data = err.response?.data;
    if (data != null) {
      if (typeof data === 'object' && data !== null && ('failureLog' in data || 'message' in data)) {
        const obj = data as { message?: string; failureLog?: string };
        const enhanced = new Error(obj.message || err.message || 'Refresh InfraUser failed') as RefreshInfraUsersError;
        enhanced.message = obj.message || enhanced.message;
        if (obj.failureLog) enhanced.failureLog = obj.failureLog;
        if (obj.message) enhanced.responseMessage = obj.message;
        console.error('Refresh InfraUser Failed:', enhanced.message, obj.failureLog ? '\n' + obj.failureLog : '');
        throw enhanced;
      }
      if (typeof data === 'string') {
        const enhanced = new Error(data) as RefreshInfraUsersError;
        enhanced.failureLog = data;
        console.error('Refresh InfraUser Failed:', data);
        throw enhanced;
      }
    }
    console.error('Refresh InfraUser Failed:', error);
    throw error;
  }
};

export const getInfraUserSummary = async () => {
  try {
    console.log('Executing /infrauser/summary API Get call');
    const response = await api.get('/infrauser/summary');
    // console.log('InfraUser Summary Response:', response);
    return response;  // Return the response
  } catch (error) {
    console.error('InfraUser Summary API Failed:', error);
    throw error;
  }
};

export interface LoginActivityWeekly {
  loginsCreated: number;
  loginsDisabled: number;
  loginsEnabled: number;
  totalAccountExpired: number;
  expiredLastWeek: number;
  expiryExtended: number;
}

export interface LoginActivityTrendDay {
  date: string;
  usersGenerated: number;
  loginsDisabled: number;
  loginsEnabled: number;
  accountExpired: number;
}

export interface LoginActivityReport {
  weekly: LoginActivityWeekly;
  trend: LoginActivityTrendDay[];
}

export const getLoginActivityReport = async (): Promise<LoginActivityReport> => {
  const response = await api.get<LoginActivityReport>('/infrauser/reports/login-activity');
  return response.data;
};

export const generateInfraUsersStd = async (formData: FormData) => {
  try {
    const response = await api.post('/infrauser/generatestd',
        formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        }, 
    );
    console.log('Response for generate infrauser STD: ', response);
    return response;
  } catch (error) {
    console.error('Generate InfraUser Std Failed:', error);
    throw error;
  }
};

export const generateInfraUsersLocal = async (payload: {
  selectedLicenseServerId: string;
  users: any[];
  abortOnFailure?: boolean;
  skipMountCheck?: boolean;
}) => {
  try {
    const response = await api.post('/infrauser/generate-users', payload);
    console.log('Response for generate infrauser local: ', response);
    return response;
  } catch (error) {
    console.error('Generate InfraUser Local Failed:', error);
    throw error;
  }
};

export interface AddNumUsersParams {
  targetSystem: string;
  numUser?: number;
  linuxgroup?: string;
  loginType?: string;
  prefix?: string;
  expiry?: string;
  index_start_number?: number;
}

export const addNumUsers = async (params: AddNumUsersParams): Promise<{ success: boolean; message?: string; stdout?: string; stderr?: string; blob?: Blob; filename?: string }> => {
  const response = await api.post('/infrauser/addnumusers', params, { responseType: 'arraybuffer' });
  const ct = response.headers['content-type'] || '';
  if (ct.includes('application/json')) {
    const text = typeof response.data === 'string' ? response.data : new TextDecoder().decode(response.data);
    return JSON.parse(text);
  }
  const blob = response.data instanceof ArrayBuffer ? new Blob([response.data]) : response.data;
  const cd = response.headers['content-disposition'] || '';
  const m = cd.match(/filename="?([^";\n]+)"?/);
  const filename = m ? m[1].trim() : `addnumusers_${new Date().toISOString().slice(0, 10)}.xlsx`;
  return { success: true, blob, filename };
};

// New functions to fetch all users and fetch user by ID
export const fetchAllUsers = async (): Promise<UniqueUser[]> => {
  try {
    const response = await api.get('/infrauser/ugetall');

    // Handle different response formats:
    // - Direct array when users exist: [user1, user2, ...]
    // - Structured response when no users: {success, message, data: [], count, suggestions}
    if (Array.isArray(response.data)) {
      // Direct array response (users exist)
      return response.data;
    } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Structured response (no users found)
      return Array.isArray(response.data.data) ? response.data.data : [];
    } else {
      // Fallback for unexpected format
      return [];
    }
  } catch (error) {
    console.error('Fetching all unique users failed:', error);
    throw error;
  }
};

export const fetchUserById = async (userId: string): Promise<UniqueUser | null> => {
  try {
    const response = await api.get<UniqueUser[]>(`/infrauser/ugetall?userId=${userId}`);
    if ( response && response.data.length > 0) {
        // console.log("Data in UniqueUserDetails",response.data)
        return response.data[0];
    }
    return null;
  } catch (error) {
    console.error(`Fetching unique user by ID ${userId} failed:`, error);
    throw error;
  }
};

export const fetchUniqueInfraUserByUserId = async (userId: string): Promise<UniqueUser | null> => {
  try {
    const response = await api.get<UniqueUser>(`/infrauser/ugetdetail?UserId=${userId}`);
    if ( response ) {
        console.log("Data in UniqueUserDetail",response.data)
        return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Fetching unique user by ID ${userId} failed:`, error);
    throw error;
  }
};
export const fetchUniqueInfraUserDetailByEmail = async (email: string): Promise<UniqueUser | null> => {
  try {
    const response = await api.get<UniqueUser>(`/infrauser/ugetdetail?email=${email}`);
    if ( response ) {
        console.log("Data in UniqueUserDetail",response.data)
        return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Fetching unique user by ID ${email} failed:`, error);
    throw error;
  }
};

/** User-ids (login IDs) that are linked to more than one unique user (email). Use to show warnings in UI. */
export const getUserIdWithMultipleEmails = async (): Promise<string[]> => {
  try {
    const response = await api.get<{ userIdsWithMultipleEmails: string[] }>('/infrauser/user-ids-multiple-emails');
    return Array.isArray(response.data?.userIdsWithMultipleEmails) ? response.data.userIdsWithMultipleEmails : [];
  } catch (error) {
    console.error('Fetching user-ids with multiple emails failed:', error);
    return [];
  }
};

export const createUser = async (users: GenerateUser[]): Promise<void> => {
  try {
    const response = await api.post('/infrauser/createuser', users, {    //not defined yet at backend
      headers: { 'Content-Type': 'application/json' },
    });
    console.log('Response for create users: ', response);
  } catch (error) {
    console.error('Create users failed:', error);
    throw error;
  }
};

export const fetchLicensesForUserId = async (userId: string): Promise<UniqueUser | null> => {
  try {
    const response = await api.get<UniqueUser>(`/lic/getlicsforuser/${userId}`);
    if ( response ) {
        console.log("Data in GetToolsForUser: ",response.data)
        return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Fetching tools for userid ${userId} failed:`, error);
    throw error;
  }
};

// Interface to be fixed!
export const getAllLicenses = async (filter: Object): Promise<LicenseTransactionEntity[] | null> => {
  try {
    const response = await api.get<LicenseTransactionEntity[]>(`/lic/getlicenses/`, { params: filter });
    if ( response ) {
        console.log("Data in getAllLicenses: ",response.data)
        return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Fetching licenses failed:`, error);
    throw error;
  }
};

export const getLicServers = async (filter: any): Promise<LicServer[] | null> => {
  try {
    const response = await api.get<LicServer[]>(`/lic/getlicsrvrs/`);
    if ( response ) {
        console.log("Data in getLicServers: ",response.data)
        return response.data;
    }
    return null;
  } catch (error) {
    console.error(`Fetching licenses servers failed:`, error);
    throw error;
  }
};

export const getAllTools = async (): Promise<ToolEntity[]> => {
  try {
    const response = await api.get<ToolEntity[]>('/lic/getalltools');
    return response.data;
  } catch (error) {
    console.error('Fetching tools failed:', error);
    throw error;
  }
};

export const getLicenseCountsForUsers = async (): Promise<{ [userId: string]: number }> => {
  try {
    const response = await api.get<{ [userId: string]: number }>('/lic/getlicensecounts');
    return response.data;
  } catch (error) {
    console.error('Fetching license counts failed:', error);
    throw error;
  }
};

export const deleteLicensesFromDatabase = async (licenseIds: number[], source?: string): Promise<void | any> => {
    try {
        const requestBody = {
            ids: licenseIds,
            source: source
        };
        console.log('Request body for delete licenses:', requestBody);
        const response = await api.post('/lic/deletelicenses', requestBody, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for delete licenses: ', response);
        return response.data;
    } catch (error) {
        console.error('Delete licenses failed:', error);
        throw error;
    }
};

export const generateNfsHome = async (userId: string): Promise<{
    ok: boolean; simulated: boolean; ipaSimulated: boolean; homeSimulated: boolean;
    uidNumber?: number; gidNumber?: number; homePath: string; homeReady: boolean; homeProvisionedAt: string;
}> => {
    const response = await api.post(`/infrauser/${encodeURIComponent(userId)}/generate-nfs-home`);
    return response.data;
};

export interface VncTarget {
    id: number;
    serverInfo: string;
    serverFqdn: string;
    sshPort: number;
    vncPort: number;
}

export const getVncTargets = async (userId: string): Promise<VncTarget[]> => {
    const response = await api.get(`/infrauser/${encodeURIComponent(userId)}/vnc-targets`);
    return response.data;
};

export const disableLicenses = async (licenseIds: number[], source?: string): Promise<any> => {
    try {
        const requestBody = {
            ids: licenseIds,
            source: source // Explicit field assignment
        };
        console.log('Request body for disable licenses:', requestBody);
        const response = await api.post('/lic/disablelicenses', requestBody, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for disable licenses: ', response);
        return response.data;
    } catch (error) {
        console.error('Disable licenses failed:', error);
        throw error;
    }
};

// Explicit page-specific functions for better source attribution
export const disableLicensesFromUniqueUserDetails = async (licenseIds: number[]): Promise<any> => {
    return disableLicenses(licenseIds, "Gui-UniqueUserDetails");
};

export const disableLicensesFromLicenseManagementPage = async (licenseIds: number[]): Promise<any> => {
    return disableLicenses(licenseIds, "Gui-LicenseManagementPage");
};

export const enableLicenses = async (licenseIds: number[], source?: string): Promise<any> => {
    try {
        const requestBody = {
            ids: licenseIds,
            source: source // Explicit field assignment
        };
        console.log('Request body for enable licenses:', requestBody);
        const response = await api.post('/lic/enablelicenses', requestBody, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for enable licenses: ', response);
        return response.data;
    } catch (error) {
        console.error('Enable licenses failed:', error);
        throw error;
    }
};

// Explicit page-specific functions for better source attribution
export const enableLicensesFromUniqueUserDetails = async (licenseIds: number[]): Promise<any> => {
    return enableLicenses(licenseIds, "Gui-UniqueUserDetails");
};

export const enableLicensesFromLicenseManagementPage = async (licenseIds: number[]): Promise<any> => {
    return enableLicenses(licenseIds, "Gui-LicenseManagementPage");
};

// Extension Request APIs
export const requestLicenseExtension = async (requestData: {
    licenseIds: number[],
    extensionDate: string,
    source?: string,
    remarks?: string
}): Promise<any> => {
    try {
        console.log('Request body for license extension:', requestData);
        const response = await api.post('/lic/request-extension', requestData, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for license extension request: ', response);
        return response.data;
    } catch (error) {
        console.error('License extension request failed:', error);
        throw error;
    }
};

export const approveExtension = async (extensionRequestId: number): Promise<any> => {
    try {
        console.log('Approving extension request:', extensionRequestId);
        const response = await api.put(`/lic/extension-request/${extensionRequestId}/approve`, {}, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for extension approval: ', response);
        return response.data;
    } catch (error) {
        console.error('Extension approval failed:', error);
        throw error;
    }
};

export const rejectExtension = async (extensionRequestId: number, rejectionReason?: string): Promise<any> => {
    try {
        console.log('Rejecting extension request:', extensionRequestId);
        const response = await api.put(`/lic/extension-request/${extensionRequestId}/reject`, {
            rejectionReason
        }, {
            headers: { 'Content-Type': 'application/json' },
        });
        console.log('Response for extension rejection: ', response);
        return response.data;
    } catch (error) {
        console.error('Extension rejection failed:', error);
        throw error;
    }
};

export const getPendingExtensions = async (): Promise<any[]> => {
    try {
        console.log('Fetching pending extension requests');
        const response = await api.get('/lic/extension-requests/pending');
        console.log('Response for pending extensions: ', response);
        return response.data;
    } catch (error) {
        console.error('Fetching pending extensions failed:', error);
        throw error;
    }
};

export const getAllExtensionRequests = async (params?: {
    status?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    offset?: number;
}): Promise<any[]> => {
    try {
        console.log('Fetching all extension requests with params:', params);
        const response = await api.get('/lic/extension-requests', { params });
        console.log('Response for all extension requests: ', response);
        return response.data;
    } catch (error) {
        console.error('Fetching all extension requests failed:', error);
        throw error;
    }
};

export const addLicensesToUsers = async (operator: string, requestParams: any) : Promise<Result> => {
    const requestBody = {
        operator,
        ...requestParams
    };
    console.log('Request body for add licenses:', requestBody);
  try {
    const response: Result = await api.post('/lic/add', requestBody, {    //not defined yet at backend
      headers: { 'Content-Type': 'application/json' },
    });
    console.log('Response for add licenses for users: ', response.data);
    return response;
    // return response.data;
  } catch (error) {
    console.error('Create users failed:', error);
    throw error;
  }
    // const result : Result = {
    //     status: 'success',
    //     statusText: 'OK',
    //     data: {},
    //     message: 'Licenses added successfully',
    //     error: '',
    //     config: {},
    //     headers: {},
    //     errorMessage: '',
    //     errorCode: '',
    //     errorDetails: '',
    //     errorStack: '',
    //     errorType: ''
    // }
    // return result
};

export const removeLicensesFromUsers = async (requestBody: any) => {
  return api.post('/lic/remove', requestBody);
};

export const updateLicensesForUsers = async (requestBody: any) => {
  return api.post('/lic/update', requestBody);
};

export const syncOptFileToLicServer = async (operator: string, requestParams: any) => {
    const requestBody = {
        operator,
        ...requestParams
    };
    try {
        const response: Result = await api.post('/lic/syncoptfile', requestBody, {
            headers: { 'Content-Type': 'application/json' },
        });
        return response;
    } catch (error) {
        console.error('Syncing of Lic Server failed! ', error);
        throw error;
    }
};
export const rereadLicServer = async (operator: string, requestParams: any) => {
    const requestBody = {
        operator,
        ...requestParams
    };
    try {
        const response: Result = await api.post('/lic/rereadlicenses', requestBody, {
            headers: { 'Content-Type': 'application/json' },
        });
        return response;
    } catch (error) {
        console.error('Syncing of Lic Server failed! ', error);
        throw error;
    }
};

// License Server Config — CRUD
export const addLicServer = async (data: Partial<import('../types').LicServer>): Promise<import('../types').LicServer> => {
    const response = await api.post('/lic/licservers', data, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
};

export const updateLicServer = async (id: number, data: Partial<import('../types').LicServer>): Promise<import('../types').LicServer> => {
    const response = await api.put(`/lic/licservers/${id}`, data, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
};

export const deleteLicServer = async (id: number): Promise<void> => {
    await api.delete(`/lic/licservers/${id}`);
};

// License File Update
export const processLicenseFile = async (server: string, fileContent: string): Promise<{ processedContent: string; lineCount: number; patchedLines: { lineNumber: number; type: string; newLine: string }[]; actualHostname: string }> => {
    const response = await api.post('/lic/process-license-file', { server, fileContent }, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
};

export const applyLicenseFile = async (server: string, processedContent: string): Promise<{ writeResult: any; rereadStdout: string; rereadStderr: string }> => {
    const response = await api.post('/lic/apply-license-file', { server, processedContent }, { headers: { 'Content-Type': 'application/json' } });
    return response.data;
};

export const getAllInfraUsers = async (queryArray?: string[]): Promise<any> => {
  try {
    // Convert array to query string
    const queryString = queryArray && queryArray.length > 0 ? 
      '?' + queryArray.join('&') : '';
    
    const response = await api.get(`/infrauser/getall${queryString}`);
    
    // Handle response formats (same as before)
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return Array.isArray(response.data.data) ? response.data.data : [];
    } else {
      return [];
    }
  } catch (error) {
    console.error('Fetching all infra users failed:', error);
    throw error;
  }
};

export const getFetchRecords = async (): Promise<any[]> => {
  try {
    const response = await api.get('/infrauser/getfetches');
    
    // Handle structured response format:
    // {success, message, data: [], count, suggestions}
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      // Structured response (always used for getfetches)
      return Array.isArray(response.data.data) ? response.data.data : [];
    } else if (Array.isArray(response.data)) {
      // Fallback for direct array response
      return response.data;
    } else {
      // Fallback for unexpected format
      return [];
    }
  } catch (error) {
    console.error('Fetching fetch records failed:', error);
    throw error;
  }
};

/** Associate email with a login ID: create or use existing unique user and link all infra_user records with that UserId. */
export const associateEmailForLoginId = async (
  userId: string,
  email: string
): Promise<{ success: boolean; message?: string; uniqueUserId?: number; email?: string; affected?: number }> => {
  const response = await api.post('/infrauser/associate-email', { userId, email });
  return response.data;
};

/** Get VNC base port per server (actual VNC port = base + display; refresh returns display). */
export const getVncBasePorts = async (servers: Array<{ host: string; sshPort: number }>): Promise<Record<string, number>> => {
  if (!servers?.length) return {};
  try {
    const response = await api.post<{ success: boolean; bases?: Record<string, number> }>('/infrauser/vnc-base-ports', { servers });
    return response.data?.bases ?? {};
  } catch (error) {
    console.error('Fetching VNC base ports failed:', error);
    return {};
  }
};

// Transaction Audit Logs API
export interface TransactionLog {
  id: number;
  sid?: string; // System ID
  TransactionId?: string; // UUID identifier
  infraMachine?: string; // Backend server machine
  transactionType: string; // Now flexible - can be any transaction type
  requestingUserId: string; // Changed from userId
  transactionStatus?: string; // Status: Pending/InProgress/Completed/Failed
  refTable?: string; // Reference table name
  records?: string; // Affected record IDs
  TransactionRequestTime: string; // Request timestamp
  remarks?: string; // Additional remarks
  machineIP?: string; // Client IP address
  responseText?: string; // JSON response metadata
  toolName?: string; // Tool affected by operation
  licenseCount?: number; // Number of licenses affected
  serverInfo?: string; // Target server information
  licSrvrConnectStr?: string; // License server connection string
}

export const getTransactionLogs = async (params?: {
  transactionType?: string;
  requestingUserId?: string; // Changed from userId
  toolName?: string;
  limit?: number;
  offset?: number;
}): Promise<TransactionLog[]> => {
  try {
    const response = await api.get<TransactionLog[]>('/lic/transactionlogs', { params });
    return response.data;
  } catch (error) {
    console.error('Fetching transaction logs failed:', error);
    throw error;
  }
};

// Get operation status for progress tracking
export const getOperationStatus = async (operationId: string) => {
  try {
    const response = await api.get(`/infrauser/operation-status/${operationId}`);
    return response.data;
  } catch (error) {
    console.error('Fetching operation status failed:', error);
    throw error;
  }
};

// Download result XLSX for a completed bulk user generation operation (like Add N Users)
export const downloadOperationResult = async (operationId: string): Promise<{ blob: Blob; filename: string } | null> => {
  try {
    const response = await api.get(`/infrauser/operation-result/${operationId}/download`, { responseType: 'arraybuffer' });
    const ct = response.headers['content-type'] || '';
    if (ct.includes('application/json')) {
      const text = typeof response.data === 'string' ? response.data : new TextDecoder().decode(response.data);
      const json = JSON.parse(text);
      if (json.message) throw new Error(json.message);
      return null;
    }
    const blob = response.data instanceof ArrayBuffer ? new Blob([response.data]) : response.data;
    const cd = response.headers['content-disposition'] || '';
    const m = cd.match(/filename="?([^";\n]+)"?/);
    const filename = m ? m[1].trim() : `user_generation_result_${operationId.slice(-8)}.xlsx`;
    return { blob, filename };
  } catch (error) {
    console.error('Download operation result failed:', error);
    throw error;
  }
};

// Download sample template Excel file
export const downloadSampleTemplate = async () => {
  try {
    const response = await api.get('/infrauser/download-template', {
      responseType: 'blob', // Important for file download
    });

    // Create a download link and trigger download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    // Get filename from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'user_generation_template.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename };
  } catch (error) {
    console.error('Downloading sample template failed:', error);
    throw error;
  }
};

// Validate users for duplicates before processing
export const validateUsersForDuplicates = async (userData: Array<{
  email?: string;
  userid?: string;
  [key: string]: any;
}>) => {
  const response = await api.post('/infrauser/validate-duplicates', { users: userData });
  return response.data;
};

// Pre-flight check on remote servers (passwd + /opt/sms/cfg/users) before bulk generation
export const preflightServers = async (users: Array<{
  userid?: string;
  userId?: string;
  puttyFQDN?: string;
  vncFQDN?: string;
  system?: string;
  'putty-fqdn'?: string;
  'vnc-fqdn'?: string;
  'system-type'?: string;
}>): Promise<{ ok: boolean; warnings: Array<{ server: string; userExists: string[]; configExists: string[] }>; message: string }> => {
  const response = await api.post('/infrauser/preflight-servers', { users });
  return response.data;
};

// Get all available transaction types
export const getTransactionTypes = async (): Promise<string[]> => {
  try {
    const response = await api.get<string[]>('/lic/transactionlogs/types');
    return response.data;
  } catch (error) {
    console.error('Fetching transaction types failed:', error);
    return []; // Return empty array on error
  }
};

// License History API
export const getLicenseHistory = async (licenseId: number): Promise<any> => {
  try {
    const response = await api.get(`/lic/licenses/${licenseId}/history`);
    return response.data;
  } catch (error) {
    console.error('Fetching license history failed:', error);
    throw error;
  }
};

// Infra Server Management APIs
export const getInfraServers = async (): Promise<any[]> => {
  try {
    const response = await api.get('/lic/infraservers');
    return response.data;
  } catch (error) {
    console.error('Fetching infra servers failed:', error);
    return [];
  }
};

export const addInfraServer = async (data: {
  hostName: string;
  port: number;
  remarks?: string;
  capabilities?: string | null;
  k3sPoolLabel?: string | null;
  site?: string | null;
}): Promise<any> => {
  const response = await api.post('/lic/infraservers', data);
  return response.data;
};

export const updateInfraServer = async (
  id: number,
  data: {
    hostName?: string;
    port?: number;
    remarks?: string;
    baseVncPort?: number | null;
    capabilities?: string | null;
    k3sPoolLabel?: string | null;
    site?: string | null;
  }
): Promise<any> => {
  const response = await api.put(`/lic/infraservers/${id}`, data);
  return response.data;
};

export const syncVncBasePorts = async (): Promise<{ success: boolean; message?: string }> => {
  const response = await api.post('/infrauser/sync-vnc-base-ports');
  return response.data;
};

export const deleteInfraServer = async (id: number): Promise<void> => {
  await api.delete(`/lic/infraservers/${id}`);
};

// Update Unique Users Information API
export const updateUniqueUsers = async (): Promise<any> => {
  try {
    console.log('Updating unique users information');
    const response = await api.post('/infrauser/update-unique-users');
    console.log('Update unique users response:', response);
    return response.data;
  } catch (error) {
    console.error('Update unique users failed:', error);
    throw error;
  }
};

// Deployment version (commit, date, author, tag) for About/Version modal
export interface DeploymentVersion {
  commitHash: string | null;
  commitShort: string | null;
  author: string | null;
  authorEmail: string | null;
  date: string | null;
  subject: string | null;
  body: string | null;
  tag: string | null;
  generatedAt: string | null;
  message?: string;
}

export const getDeploymentVersion = async (): Promise<DeploymentVersion> => {
  // Try /version first (same pattern as /health; often proxied), then /api/version
  try {
    const response = await api.get<DeploymentVersion>('/version');
    return response.data;
  } catch (err: any) {
    if (err?.response?.status === 404) {
      const r = await api.get<DeploymentVersion>('/api/version');
      return r.data;
    }
    throw err;
  }
};

export default api;


/*
export const addLicensesToUsers = async ( users: UniqueUser[], tools: ToolEntity[], numLicenses: number ) : Promise<Result | any> => {
    console.log('Request body for add licenses:', { users, tools, numLicenses });
  try {
    const response = await api.post('/lic/add', {users, tools, numLicenses}, {    //not defined yet at backend
      headers: { 'Content-Type': 'application/json' },
    });
    console.log('Response for add licenses for users: ', response);
    return response;
  } catch (error) {
    console.error('Create users failed:', error);
    throw error;
  }
};
*/
